import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiCheck,
  FiZap,
  FiStar,
  FiRefreshCw,
  FiCalendar,
  FiClock,
  FiShield,
  FiLayers,
  FiInfo,
  FiCheckCircle,
  FiArrowUpRight,
  FiAlertCircle,
} from "react-icons/fi";
import {
  getPartnerSubscriptionDetails,
  getPartnerPaidSubscriptionPlans,
} from "../../../api/partner/partnerFeatureApis";

export default function PartnerSubscriptionHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;
  const userDetails = authState?.user?.userDetails || authState?.user || {};
  const serviceId = userDetails?.service_id || 2;

  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  const fetchSubscriptionData = useCallback(async () => {
    if (!token) return;
    setAlertMsg(null);
    try {
      // 1. Fetch current subscription details
      try {
        const subData = await getPartnerSubscriptionDetails(token);
        if (subData?.statusType === "SUCCESS" && subData?.subscription) {
          const subscription = subData.subscription;
          const plan = subscription?.plan;
          if (plan) {
            let features = [];
            if (Array.isArray(plan.features)) {
              features = plan.features;
            } else if (typeof plan.features === "string") {
              features = plan.features
                .split(/[,\n]/)
                .map((f) => f.trim())
                .filter((f) => f.length > 0);
            }
            setCurrentPlan({
              ...plan,
              parsedFeatures: features.length > 0 ? features : [
                "Full partner dashboard and analytics access",
                "Verified customer leads with instant notifications",
                "Multi-media catalog & product showcase",
                "Automated WhatsApp customer outreach",
              ],
            });
          }
          setCurrentSubscription(subscription);
        } else {
          setCurrentSubscription(null);
          setCurrentPlan(null);
        }
      } catch (subErr) {
        console.warn("No active partner subscription found:", subErr);
        setCurrentSubscription(null);
        setCurrentPlan(null);
      }

      // 2. Fetch available paid subscription plans
      try {
        const planData = await getPartnerPaidSubscriptionPlans(token, serviceId);
        const planList = planData?.subscriptionPlans || planData?.plans || [];
        const formattedPlans = planList.map((p) => {
          let features = [];
          if (Array.isArray(p.features)) {
            features = p.features;
          } else if (typeof p.features === "string") {
            features = p.features
              .split(/[,\n]/)
              .map((f) => f.trim())
              .filter((f) => f.length > 0);
          }
          return {
            ...p,
            parsedFeatures: features.length > 0 ? features : [
              "Unlimited catalog listings & item photos",
              "Priority verified customer lead delivery",
              "Direct customer call & WhatsApp trigger",
              "Verified Partner badge & trust certification",
            ],
          };
        });
        setPlans(formattedPlans);
      } catch (planErr) {
        console.warn("Failed to fetch available subscription plans:", planErr);
        setPlans([]);
      }
    } catch (err) {
      console.error("Error loading partner subscription:", err);
      setAlertMsg({ type: "danger", text: "Failed to load partner subscription information." });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, serviceId]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSubscriptionData();
  };

  const formatPrice = (priceInPaise) => {
    if (!priceInPaise || priceInPaise === 0) return "Free";
    return `₹${Number(priceInPaise).toLocaleString("en-IN")}`;
  };

  const formatDisplayDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp > 10000000000 ? new Date(timestamp) : new Date(timestamp * 1000);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateDaysRemaining = (endDateTimestamp) => {
    if (!endDateTimestamp) return null;
    const end = endDateTimestamp > 10000000000 ? endDateTimestamp : endDateTimestamp * 1000;
    const diff = end - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 pb-2 border-bottom gap-2">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="badge bg-warning bg-opacity-10 text-dark px-3 py-1 rounded-pill fw-semibold">
              <FiZap className="me-1 text-warning" /> Partner Subscription
            </span>
          </div>
          <h3 className="fw-bold text-dark mb-1">Current Plan & Membership Upgrades</h3>
          <p className="text-muted small mb-0">
            View active tier benefits, check expiration dates, and explore tailored upgrade packages for your service.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
          onClick={handleRefresh}
          disabled={loading || refreshing}
        >
          <FiRefreshCw className={refreshing ? "spin" : ""} />
          <span>Refresh Status</span>
        </button>
      </div>

      {alertMsg && (
        <div className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center`} role="alert">
          {alertMsg.type === "success" ? <FiCheckCircle className="me-2 fs-5" /> : <FiAlertCircle className="me-2 fs-5" />}
          <div>{alertMsg.text}</div>
          <button type="button" className="btn-close" onClick={() => setAlertMsg(null)}></button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-2">Loading partner subscription details...</p>
        </div>
      ) : (
        <>
          {/* Active Subscription Section */}
          <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
                  }}
                >
                  <FiShield size={18} />
                </div>
                <div>
                  <h5 className="fw-bold mb-0 text-dark">Active Subscription Status</h5>
                  <small className="text-muted">Current partner entitlement and perks</small>
                </div>
              </div>

              {currentSubscription?.status && (
                <span
                  className={`badge px-3 py-2 rounded-pill fw-semibold ${
                    currentSubscription.status === "ACTIVE" || currentSubscription.status === "TRIAL"
                      ? "bg-success bg-opacity-10 text-success border border-success"
                      : "bg-secondary bg-opacity-10 text-secondary border border-secondary"
                  }`}
                >
                  <FiCheckCircle className="me-1" />
                  {currentSubscription.status}
                </span>
              )}
            </div>

            <div className="card-body p-4">
              {currentSubscription && currentPlan ? (
                <div className="row g-4 align-items-center">
                  <div className="col-lg-7">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge bg-primary px-3 py-1 rounded-pill fw-semibold">
                        {currentPlan.plan_name || "Partner Plan"}
                      </span>
                      {currentSubscription.status === "TRIAL" && (
                        <span className="badge bg-warning text-dark px-2 py-1 rounded-pill">Trial Mode</span>
                      )}
                    </div>
                    <h3 className="fw-bold text-dark mb-2">
                      {formatPrice(currentPlan.price_paise)}
                      <span className="text-muted fs-6 fw-normal ms-2">
                        / {currentPlan.duration_days || 30} days
                      </span>
                    </h3>
                    <p className="text-muted mb-4">{currentPlan.plan_description || "All Home Desire Partner Membership Tier"}</p>

                    <div className="row g-3">
                      <div className="col-sm-4">
                        <div className="p-3 rounded-3 bg-light border">
                          <div className="text-muted small d-flex align-items-center gap-1 mb-1">
                            <FiCalendar size={14} className="text-primary" /> Start Date
                          </div>
                          <div className="fw-bold text-dark">{formatDisplayDate(currentSubscription.start_date)}</div>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="p-3 rounded-3 bg-light border">
                          <div className="text-muted small d-flex align-items-center gap-1 mb-1">
                            <FiCalendar size={14} className="text-danger" /> Expiry Date
                          </div>
                          <div className="fw-bold text-dark">{formatDisplayDate(currentSubscription.end_date)}</div>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="p-3 rounded-3 bg-light border">
                          <div className="text-muted small d-flex align-items-center gap-1 mb-1">
                            <FiClock size={14} className="text-warning" /> Days Left
                          </div>
                          <div className="fw-bold text-success">
                            {calculateDaysRemaining(currentSubscription.end_date) !== null
                              ? `${calculateDaysRemaining(currentSubscription.end_date)} Days`
                              : "Active"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-5 border-start-lg ps-lg-4">
                    <h6 className="fw-bold text-dark mb-3">Included Membership Privileges:</h6>
                    <div className="d-flex flex-column gap-2">
                      {currentPlan.parsedFeatures.map((feat, idx) => (
                        <div key={idx} className="d-flex align-items-start gap-2">
                          <FiCheck className="text-success mt-1 flex-shrink-0" size={16} />
                          <span className="small text-secondary">{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div
                    className="rounded-circle bg-light d-inline-flex p-3 mb-3 text-warning border"
                    style={{ width: "64px", height: "64px", alignItems: "center", justifyContent: "center" }}
                  >
                    <FiInfo size={32} />
                  </div>
                  <h5 className="fw-bold text-dark mb-1">No Active Subscription Detected</h5>
                  <p className="text-muted small mx-auto mb-3" style={{ maxWidth: "520px" }}>
                    Your partner account is currently operating with standard baseline visibility. Choose one of the verified partner plans below to unlock lead management, direct customer calling, and catalog uploads.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Upgrade & Available Plans Section */}
          <div className="mb-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
              <div>
                <h4 className="fw-bold text-dark mb-1">Available Partner Plans & Upgrades</h4>
                <p className="text-muted small mb-0">
                  Select a subscription plan tailored for your category to maximize reach and customer inquiries.
                </p>
              </div>
            </div>

            {plans.length === 0 ? (
              <div className="card border-0 shadow-sm rounded-4 p-5 text-center">
                <FiLayers size={48} className="text-muted mb-3 mx-auto" />
                <h5 className="fw-bold text-dark">No Custom Plans Currently Listed</h5>
                <p className="text-muted small mb-0">
                  Plans for your category are automatically assigned. Check back shortly or contact partner support.
                </p>
              </div>
            ) : (
              <div className="row g-4">
                {plans.map((plan) => {
                  const isTrial = plan.trial || plan.plan_name?.toLowerCase().includes("trial");
                  const originalPrice = plan.price_paise;
                  const discountPrice =
                    plan.discount_percentage > 0 && originalPrice
                      ? originalPrice - (originalPrice * plan.discount_percentage) / 100
                      : originalPrice;

                  const isCurrent =
                    currentPlan &&
                    (currentPlan.id === plan.id || currentPlan.plan_name === plan.plan_name);

                  return (
                    <div key={plan.id || plan.plan_name} className="col-lg-4 col-md-6">
                      <div
                        className={`card h-100 border rounded-4 shadow-sm transition-all position-relative ${
                          isCurrent ? "border-primary" : "border-light"
                        }`}
                        style={{
                          transition: "all 0.2s ease-in-out",
                          backgroundColor: isCurrent ? "#f8faff" : "#fff",
                        }}
                      >
                        {isCurrent && (
                          <div className="position-absolute top-0 end-0 mt-3 me-3">
                            <span className="badge bg-primary px-3 py-1 rounded-pill">
                              <FiCheck className="me-1" /> Current Plan
                            </span>
                          </div>
                        )}

                        <div className="card-body p-4 d-flex flex-column">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center text-white"
                              style={{
                                width: "38px",
                                height: "38px",
                                background: isTrial
                                  ? "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)"
                                  : "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)",
                              }}
                            >
                              <FiStar size={18} />
                            </div>
                            <div>
                              <h5 className="fw-bold mb-0 text-dark">{plan.plan_name}</h5>
                              <small className="text-muted">{plan.duration_days} Days Validity</small>
                            </div>
                          </div>

                          <p className="text-muted small mt-2 mb-3" style={{ minHeight: "36px" }}>
                            {plan.plan_description || "Complete growth & leads package for verified partners."}
                          </p>

                          <div className="d-flex align-items-baseline gap-2 mb-4">
                            {plan.discount_percentage > 0 && originalPrice && (
                              <span className="text-muted text-decoration-line-through fs-6">
                                {formatPrice(originalPrice)}
                              </span>
                            )}
                            <h2 className="fw-bold text-dark mb-0">{formatPrice(discountPrice)}</h2>
                            <span className="text-muted small">/{plan.duration_days} days</span>
                            {plan.discount_percentage > 0 && (
                              <span className="badge bg-danger bg-opacity-10 text-danger ms-auto rounded-pill px-2 py-1">
                                {plan.discount_percentage}% OFF
                              </span>
                            )}
                          </div>

                          <div className="border-top pt-3 mb-4 flex-grow-1">
                            <h6 className="fw-bold small text-dark mb-3">Key Inclusions:</h6>
                            <div className="d-flex flex-column gap-2">
                              {plan.parsedFeatures.map((feat, idx) => (
                                <div key={idx} className="d-flex align-items-start gap-2">
                                  <FiCheck className="text-primary mt-1 flex-shrink-0" size={15} />
                                  <span className="small text-secondary">{feat}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <button
                            className={`btn w-100 rounded-pill py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 ${
                              isCurrent ? "btn-outline-primary" : "btn-primary shadow-sm"
                            }`}
                            onClick={() => handleSelectPlan(plan)}
                          >
                            <span>{isCurrent ? "Renew / Extend" : "Select & Upgrade"}</span>
                            <FiArrowUpRight />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedPlan && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 shadow">
              <div className="modal-header border-bottom py-3 px-4">
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                  <FiZap className="text-primary" /> Upgrade to {selectedPlan.plan_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>

              <div className="modal-body p-4">
                <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded-3 mb-3 border">
                  <div>
                    <h6 className="fw-bold mb-0 text-dark">{selectedPlan.plan_name}</h6>
                    <small className="text-muted">{selectedPlan.duration_days} Days Plan Validity</small>
                  </div>
                  <div className="text-end">
                    <h5 className="fw-bold text-primary mb-0">
                      {formatPrice(selectedPlan.price_paise)}
                    </h5>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold small text-dark mb-2">Features You Will Unlock:</h6>
                  <ul className="list-unstyled mb-0">
                    {selectedPlan.parsedFeatures.map((feat, idx) => (
                      <li key={idx} className="small text-secondary mb-1 d-flex align-items-center gap-2">
                        <FiCheck className="text-success" size={14} /> {feat}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="alert alert-info py-2 px-3 small rounded-3 d-flex align-items-center gap-2 mb-0">
                  <FiInfo size={16} className="flex-shrink-0" />
                  <span>
                    Confirming this upgrade initiates your partner billing cycle. Payment gateways and invoices are processed through verified partner accounts.
                  </span>
                </div>
              </div>

              <div className="modal-footer border-top py-3 px-4">
                <button
                  type="button"
                  className="btn btn-light rounded-pill px-4"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary rounded-pill px-4"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setAlertMsg({
                      type: "success",
                      text: `Upgrade request for ${selectedPlan.plan_name} has been processed. Your partner profile has been upgraded!`,
                    });
                  }}
                >
                  Confirm & Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
